import PageHeader from "../../components/ui/PageHeader";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import { getLegalHistory, getMissingAcceptances, LEGAL_TYPES, LegalDocument, LegalType, MissingTenant, publishLegalDocument } from "../../service/LegalService";
import { showSuccessToast } from "../../utils/Toast";
import { orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

const ACCEPTANCE_TYPES: LegalType[] = ["terms", "dpa"];
const date = (value?: string) => (value ? new Date(value).toLocaleString("da-DK") : "-");

/**
 * Juridiske dokumenter (superadmin): the versioned terms, data processing agreement, sub-processors, privacy policy
 * and employee information template. A published version is never changed - "Ny version" publishes a new one
 * (optionally requiring new acceptance and e-mailing all customers). Also: customers missing an acceptance.
 */
export default function LegalDocuments() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [history, setHistory] = useState<LegalDocument[]>([]);
  const [missing, setMissing] = useState<MissingTenant[]>([]);
  const [editing, setEditing] = useState<{ type: LegalType; title: string; content: string; draft: boolean; requiresAcceptance: boolean; notify: boolean; effectiveAt: string } | null>(null);
  const [showHistory, setShowHistory] = useState<LegalType | null>(null);

  const load = () => {
    getLegalHistory().then(setHistory);
    getMissingAcceptances().then(setMissing);
  };
  useEffect(load, []);

  const current = (type: LegalType) => history.filter((doc) => doc.type === type).sort((a, b) => b.version - a.version)[0];

  const startNewVersion = (type: LegalType) => {
    const doc = current(type);
    setEditing({
      type,
      title: doc?.title || "",
      content: doc?.content || "",
      draft: doc?.draft ?? true,
      requiresAcceptance: ACCEPTANCE_TYPES.includes(type),
      notify: type === "subprocessors" || ACCEPTANCE_TYPES.includes(type),
      effectiveAt: new Date().toISOString().slice(0, 10),
    });
  };

  const publish = async () => {
    if (!editing) return;
    const saved = await publishLegalDocument({ ...editing, effectiveAt: new Date(editing.effectiveAt).toISOString() });
    if (saved) {
      showSuccessToast(t("legal.published", { version: saved.version }), isMobile);
      setEditing(null);
      load();
    }
  };

  const exportMissing = () => {
    const rows = [["Firma", "CVR", "E-mail", "Mangler"], ...missing.map((row) => [row.companyName, row.cvr, row.email, row.missing.map((doc) => `${doc.title} v${doc.version}`).join(", ")])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.download = "mangler-accept.csv";
    link.click();
  };

  return (
    <Layout>
      <PageHeader title={t("legal.title")} description={t("legal.intro")} />

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("legal.document")}</StyledTableCell>
              <StyledTableCell>{t("legal.version")}</StyledTableCell>
              <StyledTableCell>{t("legal.effective")}</StyledTableCell>
              <StyledTableCell>{t("legal.status")}</StyledTableCell>
              <StyledTableCell align="right">{t("legal.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {LEGAL_TYPES.map((type) => {
              const doc = current(type);
              return (
                <StyledTableRow key={type}>
                  <TableCell>{doc?.title || t(`legal.types.${type}`)}</TableCell>
                  <TableCell>{doc ? doc.version : "-"}</TableCell>
                  <TableCell>{date(doc?.effectiveAt)}</TableCell>
                  <TableCell>
                    {doc?.draft ? t("legal.draft") : t("legal.final")}
                    {doc?.requiresAcceptance ? ` · ${t("legal.requiresAcceptance")}` : ""}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button size="small" sx={outlinedButton} variant="outlined" onClick={() => setShowHistory(type)}>
                      {t("legal.history")}
                    </Button>{" "}
                    <Button size="small" sx={orangeButton} variant="contained" onClick={() => startNewVersion(type)}>
                      {t("legal.newVersion")}
                    </Button>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>

      <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="h6">{t("legal.missingTitle", { count: missing.length })}</Typography>
        {missing.length > 0 && (
          <Button variant="outlined" sx={outlinedButton} onClick={exportMissing}>
            {t("legal.export")}
          </Button>
        )}
      </Box>
      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("legal.company")}</StyledTableCell>
              <StyledTableCell>CVR</StyledTableCell>
              <StyledTableCell>{t("legal.email")}</StyledTableCell>
              <StyledTableCell>{t("legal.missing")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {missing.map((row) => (
              <StyledTableRow key={row.tenantId}>
                <TableCell>{row.companyName}</TableCell>
                <TableCell>{row.cvr}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.missing.map((doc) => `${doc.title} v${doc.version}`).join(", ")}</TableCell>
              </StyledTableRow>
            ))}
            {!missing.length && (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center" }}>
                  {t("legal.noneMissing")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <Dialog open={Boolean(showHistory)} onClose={() => setShowHistory(null)} maxWidth="md" fullWidth>
        <DialogTitle>{showHistory && (current(showHistory)?.title || t(`legal.types.${showHistory}`))}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>{t("legal.version")}</StyledTableCell>
                <StyledTableCell>{t("legal.effective")}</StyledTableCell>
                <StyledTableCell>{t("legal.publishedAt")}</StyledTableCell>
                <StyledTableCell>{t("legal.status")}</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history
                .filter((doc) => doc.type === showHistory)
                .map((doc) => (
                  <StyledTableRow key={doc.version}>
                    <TableCell>{doc.version}</TableCell>
                    <TableCell>{date(doc.effectiveAt)}</TableCell>
                    <TableCell>{date(doc.createdAt)}</TableCell>
                    <TableCell>
                      {doc.draft ? t("legal.draft") : t("legal.final")}
                      {doc.requiresAcceptance ? ` · ${t("legal.requiresAcceptance")}` : ""}
                    </TableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button sx={outlinedButton} variant="outlined" onClick={() => setShowHistory(null)}>
            {t("legal.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t("legal.newVersionTitle")}</DialogTitle>
        {editing && (
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              {t("legal.newVersionHelp", { kunde: "{{kunde}}" })}
            </Typography>
            <TextField select size="small" label={t("legal.document")} value={editing.type} sx={{ minWidth: 260, mt: 1 }} disabled>
              {LEGAL_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`legal.types.${type}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" margin="dense" label={t("legal.docTitle")} value={editing.title} onChange={(event) => setEditing({ ...editing, title: event.target.value })} />
            <TextField
              fullWidth
              multiline
              minRows={14}
              margin="dense"
              label={t("legal.content")}
              value={editing.content}
              onChange={(event) => setEditing({ ...editing, content: event.target.value })}
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />
            <TextField size="small" type="date" margin="dense" label={t("legal.effective")} InputLabelProps={{ shrink: true }} value={editing.effectiveAt} onChange={(event) => setEditing({ ...editing, effectiveAt: event.target.value })} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel control={<Switch color="warning" checked={editing.draft} onChange={(event) => setEditing({ ...editing, draft: event.target.checked })} />} label={t("legal.draftSwitch")} />
              {ACCEPTANCE_TYPES.includes(editing.type) && (
                <FormControlLabel
                  control={<Switch color="warning" checked={editing.requiresAcceptance} onChange={(event) => setEditing({ ...editing, requiresAcceptance: event.target.checked })} />}
                  label={t("legal.requiresAcceptanceSwitch")}
                />
              )}
              <FormControlLabel control={<Switch color="warning" checked={editing.notify} onChange={(event) => setEditing({ ...editing, notify: event.target.checked })} />} label={t("legal.notifySwitch")} />
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button sx={outlinedButton} variant="outlined" onClick={() => setEditing(null)}>
            {t("legal.cancel")}
          </Button>
          <Button sx={orangeButton} variant="contained" onClick={publish} disabled={!editing?.title || !editing?.content}>
            {t("legal.publish")}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
